import { useRequiredContext } from "@redotech/react-util/context";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import RechargeLogo from "@redotech/redo-icons/recharge-logo.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import {
  SubscriptionIntegration,
  SubscriptionIntegrationStatus,
  SubscriptionIntegrationStatusValue,
} from "@redotech/redo-model/subscription-integrations/subscription-integration";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { getDateString } from "@redotech/util/date-format";
import { assertNever } from "@redotech/util/type";
import { memo, useState } from "react";
import { CancelSubscriptionModal } from "./cancel-subscription-modal";
import * as subscriptionDetailsCss from "./conversation-details-subscriptions.module.css";
import { CustomerActionsDropdown } from "./customer-actions-dropdown";
import { EditAddressModal } from "./edit-address-modal";
import { EditSubscriptionModal } from "./edit-subscription-modal";
import { SkipSubscriptionModal } from "./skip-subscription-modal";
import { SubscriptionActionsDropdown } from "./subscription-actions-dropdown";
import { getFrequencyText } from "./subscription-frequency-badge";
import {
  useCancelSubscription,
  useCustomerSubscriptions,
  useSkipSubscription,
} from "./use-subscription-queries";

export const ConversationDetailsSubscriptions = memo(
  function ConversationDetailsSubscriptions({
    conversation,
  }: {
    conversation: MinimalConversation;
  }) {
    const canViewOrders = useHasPermission(
      SupportPermissions.VIEW_CUSTOMER_ORDERS,
    );
    const team = useRequiredContext(TeamContext);

    const primaryEmail = getPrimaryCustomerEmail(conversation.customer);
    const { data: subscriptionsData, isLoading } =
      useCustomerSubscriptions(primaryEmail);
    const cancelSubscription = useCancelSubscription(primaryEmail);
    const skipSubscription = useSkipSubscription(primaryEmail);

    const [editingSubscription, setEditingSubscription] =
      useState<SubscriptionIntegration | null>(null);
    const [editingAddress, setEditingAddress] =
      useState<SubscriptionIntegration | null>(null);
    const [cancelingSubscription, setCancelingSubscription] =
      useState<SubscriptionIntegration | null>(null);
    const [skippingSubscription, setSkippingSubscription] =
      useState<SubscriptionIntegration | null>(null);
    const [isExpanded, setIsExpanded] = useState(true);

    if (!canViewOrders) {
      return null;
    }

    const hasSubscriptionIntegration =
      subscriptionsData?.hasSubscriptionIntegration ?? false;

    if (!isLoading && !hasSubscriptionIntegration) {
      return null;
    }

    const subscriptions = subscriptionsData?.subscriptions || [];
    const hasSubscriptions = subscriptions.length > 0;
    const totalSpent = subscriptionsData?.totalSpent || "0.00";
    const totalCharges = subscriptionsData?.totalCharges || 0;
    const activeSubscriptionsCount = subscriptions.filter(
      (sub) => sub.status === SubscriptionIntegrationStatus.ACTIVE,
    ).length;

    const getStatusSortOrder = (
      status: SubscriptionIntegrationStatusValue,
    ): number => {
      switch (status) {
        case SubscriptionIntegrationStatus.ACTIVE:
          return 1;
        case SubscriptionIntegrationStatus.EXPIRED:
          return 2;
        case SubscriptionIntegrationStatus.CANCELLED:
          return 3;
        default:
          assertNever(status);
      }
    };

    const sortedSubscriptions = [...subscriptions].sort(
      (a, b) => getStatusSortOrder(a.status) - getStatusSortOrder(b.status),
    );

    const formatDate = (dateString?: string) => {
      if (!dateString) return "N/A";
      try {
        return getDateString(Temporal.PlainDate.from(dateString));
      } catch {
        return "N/A";
      }
    };

    const getStatusBadgeColor = (
      status: SubscriptionIntegrationStatusValue,
    ): "success" | "error" | "warning" | "gray" => {
      switch (status) {
        case SubscriptionIntegrationStatus.ACTIVE:
          return "success";
        case SubscriptionIntegrationStatus.CANCELLED:
          return "error";
        case SubscriptionIntegrationStatus.EXPIRED:
          return "warning";
        default:
          assertNever(status);
      }
    };

    const getStatusDisplayText = (
      status: SubscriptionIntegrationStatusValue,
    ): string => {
      switch (status) {
        case SubscriptionIntegrationStatus.ACTIVE:
          return "Active";
        case SubscriptionIntegrationStatus.CANCELLED:
          return "Cancelled";
        case SubscriptionIntegrationStatus.EXPIRED:
          return "Expired";
        default:
          assertNever(status);
      }
    };

    const handleEditSubscription = (sub: SubscriptionIntegration) => {
      setEditingSubscription(sub);
    };

    const handleEditAddress = (sub: SubscriptionIntegration) => {
      setEditingAddress(sub);
    };

    const handleCancelClick = (sub: SubscriptionIntegration) => {
      setCancelingSubscription(sub);
    };

    const handleSkipClick = (sub: SubscriptionIntegration) => {
      setSkippingSubscription(sub);
    };

    const handleConfirmCancel = async (subscriptionId: number) => {
      try {
        await cancelSubscription.mutateAsync({
          subscriptionId,
        });
        setCancelingSubscription(null);
        toast("Subscription cancelled successfully", { variant: "success" });
      } catch (error: any) {
        toast(`Failed to cancel subscription: ${error.message || error}`, {
          variant: "error",
        });
      }
    };

    const handleConfirmSkip = async (subscriptionId: number) => {
      try {
        await skipSubscription.mutateAsync({
          subscriptionId,
        });
        setSkippingSubscription(null);
        toast("Subscription charge skipped successfully", {
          variant: "success",
        });
      } catch (error: any) {
        toast(`Failed to skip subscription: ${error.message || error}`, {
          variant: "error",
        });
      }
    };

    const handleViewInRecharge = (subscriptionId: number) => {
      const storeUrl = team.storeUrl.replace(".myshopify.com", "");
      const rechargeUrl = `https://${storeUrl}.admin.rechargeapps.com/merchant/subscriptions/${subscriptionId}/details`;
      window.open(rechargeUrl, "_blank", "noopener,noreferrer");
    };

    const handleViewCustomerInRecharge = (customerId: number) => {
      const storeUrl = team.storeUrl.replace(".myshopify.com", "");
      const rechargeUrl = `https://${storeUrl}.admin.rechargeapps.com/merchant/customers/${customerId}`;
      window.open(rechargeUrl, "_blank", "noopener,noreferrer");
    };

    return (
      <Flex dir="column" gap="md">
        <Flex
          align="center"
          justify="space-between"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: "pointer" }}
        >
          <Flex align="center" gap="xs" justify="space-between" w="full">
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              Subscriptions
            </Text>
            {isExpanded ? (
              <ChevronUpIcon style={{ width: 12, height: 12 }} />
            ) : (
              <ChevronDownIcon style={{ width: 12, height: 12 }} />
            )}
          </Flex>
        </Flex>

        {isExpanded && isLoading && (
          <Flex align="center" justify="center" p="xl">
            <LoadingRedoAnimation />
          </Flex>
        )}

        {isExpanded && !isLoading && !hasSubscriptions && (
          <Flex align="center" justify="center" p="md">
            <Text fontSize="xs" textColor="tertiary">
              No subscriptions found
            </Text>
          </Flex>
        )}

        {isExpanded && !isLoading && hasSubscriptions && (
          <Flex dir="column" gap="sm">
            <Flex
              className={subscriptionDetailsCss.subscriptionCard}
              dir="column"
              p="md"
              w="full"
            >
              <Flex align="stretch" dir="column" gap="lg">
                <Flex align="center" justify="space-between">
                  <Flex align="center" justify="flex-start">
                    <RechargeLogo
                      className={subscriptionDetailsCss.subscriptionLogo}
                    />
                    <Text fontSize="xs" fontWeight="medium" textColor="primary">
                      Recharge
                    </Text>
                  </Flex>
                  {subscriptions[0] && (
                    <CustomerActionsDropdown
                      customerId={subscriptions[0].customerId}
                      onViewInRecharge={handleViewCustomerInRecharge}
                    />
                  )}
                </Flex>

                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  Customer details
                </Text>

                <Flex align="center" justify="space-between">
                  <Text fontSize="xs" textColor="tertiary">
                    Total spent
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" textColor="primary">
                    ${totalSpent}
                  </Text>
                </Flex>

                <Flex align="center" justify="space-between">
                  <Text fontSize="xs" textColor="tertiary">
                    Total charges
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" textColor="primary">
                    {totalCharges}
                  </Text>
                </Flex>

                <Flex align="center" justify="space-between">
                  <Text fontSize="xs" textColor="tertiary">
                    Active subscriptions
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" textColor="primary">
                    {activeSubscriptionsCount}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {sortedSubscriptions.map((subscription) => {
              const productImage = subscription.product?.images?.[0]?.url;
              const productName = subscription.product?.name || "Product";

              // Find the specific variant for this subscription
              const matchingVariant = subscription.variantExternalId
                ? subscription.product?.variants.find(
                    (v) => v.externalId === subscription.variantExternalId,
                  )
                : subscription.product?.variants?.[0];

              const variantDisplay = matchingVariant?.attributes
                ?.map((attr) => attr.value)
                .join(" / ");

              // Calculate discount if variant price is available
              const variantPrice = matchingVariant?.price ?? null;
              const subscriptionPrice = parseFloat(subscription.price);
              const discountAmount =
                variantPrice && variantPrice > subscriptionPrice
                  ? variantPrice - subscriptionPrice
                  : null;
              const discountPercentage =
                discountAmount && variantPrice
                  ? Math.round((discountAmount / variantPrice) * 100)
                  : null;

              return (
                <Flex
                  className={subscriptionDetailsCss.subscriptionCard}
                  dir="column"
                  key={subscription.id.toString()}
                  pb="md"
                  pt="xs"
                  px="md"
                  w="full"
                >
                  <Flex align="stretch" dir="column" gap="xl">
                    <Flex dir="column" gap="sm">
                      <Flex align="center" justify="space-between">
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="tertiary"
                        >
                          Subscription
                        </Text>
                        <SubscriptionActionsDropdown
                          onCancel={handleCancelClick}
                          onEdit={handleEditSubscription}
                          onEditAddress={handleEditAddress}
                          onSkip={handleSkipClick}
                          onViewInRecharge={handleViewInRecharge}
                          subscription={subscription}
                        />
                      </Flex>

                      <Flex wrap="nowrap">
                        <div className={subscriptionDetailsCss.pillsContainer}>
                          <div className={subscriptionDetailsCss.pillWrapper}>
                            <RedoBadge
                              color={getStatusBadgeColor(subscription.status)}
                              size="sm"
                              text={getStatusDisplayText(subscription.status)}
                            />
                          </div>
                        </div>
                      </Flex>

                      <Flex align="flex-start" gap="sm">
                        {productImage && (
                          <div className={subscriptionDetailsCss.imgContainer}>
                            <img alt={productName} src={productImage} />
                          </div>
                        )}
                        <Flex dir="column" gap="xs" style={{ flex: 1 }}>
                          <Text fontSize="xs" fontWeight="semibold">
                            {productName}
                          </Text>
                          {variantDisplay && (
                            <Text fontSize="xs" textColor="secondary">
                              {variantDisplay}
                            </Text>
                          )}
                          <Flex align="center" gap="md">
                            <Text fontSize="xs" textColor="tertiary">
                              Qty: {subscription.quantity}
                            </Text>
                            <Text fontSize="xs" textColor="tertiary">
                              ${subscription.price}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>
                    <Flex dir="column" gap="none">
                      <Text fontSize="xxs" textColor="tertiary">
                        Total price
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        textColor="primary"
                      >
                        $
                        {(
                          subscription.quantity * parseFloat(subscription.price)
                        ).toFixed(2)}
                      </Text>
                    </Flex>
                    <Flex dir="column" gap="none">
                      <Text fontSize="xxs" textColor="tertiary">
                        Frequency
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        textColor="primary"
                      >
                        {getFrequencyText(
                          subscription.orderIntervalFrequency ?? 1,
                          subscription.orderIntervalUnit ?? "month",
                        )}
                      </Text>
                    </Flex>
                    {discountPercentage && discountAmount && (
                      <Flex dir="column" gap="none">
                        <Text fontSize="xxs" textColor="tertiary">
                          Discount amount
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="primary"
                        >
                          {discountPercentage}% ($
                          {discountAmount.toFixed(2)})
                        </Text>
                      </Flex>
                    )}
                    {subscription.nextChargeScheduledAt && (
                      <Flex dir="column" gap="none">
                        <Text fontSize="xxs" textColor="tertiary">
                          Date of renewal
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="primary"
                        >
                          {formatDate(subscription.nextChargeScheduledAt)}
                        </Text>
                      </Flex>
                    )}
                    {subscription.cancelledAt && (
                      <Flex dir="column" gap="none">
                        <Text fontSize="xxs" textColor="tertiary">
                          Cancelled on
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="primary"
                        >
                          {formatDate(subscription.cancelledAt)}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        )}

        {editingSubscription && (
          <EditSubscriptionModal
            customerEmail={primaryEmail}
            isOpen={!!editingSubscription}
            onClose={() => setEditingSubscription(null)}
            subscription={editingSubscription}
          />
        )}

        {editingAddress && (
          <EditAddressModal
            customerEmail={primaryEmail}
            isOpen={!!editingAddress}
            onClose={() => setEditingAddress(null)}
            subscription={editingAddress}
          />
        )}

        {cancelingSubscription && (
          <CancelSubscriptionModal
            isLoading={cancelSubscription.isPending}
            isOpen={!!cancelingSubscription}
            onClose={() => setCancelingSubscription(null)}
            onConfirm={handleConfirmCancel}
            subscription={cancelingSubscription}
          />
        )}

        {skippingSubscription && (
          <SkipSubscriptionModal
            isLoading={skipSubscription.isPending}
            isOpen={!!skippingSubscription}
            onClose={() => setSkippingSubscription(null)}
            onConfirm={handleConfirmSkip}
            subscription={skippingSubscription}
          />
        )}
      </Flex>
    );
  },
);
